import { RootState } from "common/reducers";
import * as React from "react";
import { useSelector } from "react-redux";
import spinnerImg from "assets/images/spinner.gif";

export interface SpinnerProps {
  /**
   * Selector that decides whether the spinner shows. Optional: a caller that already knows it's
   * loading (e.g. it only mounts the spinner inside a `loading &&` branch) omits it and gets the
   * always-on default, rather than passing a throwaway `() => true`.
   */
  loadingSelector?: (state: RootState) => boolean;
  inline?: boolean;
}

export const Spinner: React.FC<SpinnerProps> = ({ loadingSelector = () => true, inline }) => {
  const loading = useSelector(loadingSelector);

  if (!loading) {
    return null;
  }

  if (inline) {
    return (
      <div role="status" aria-live="polite" aria-busy="true" aria-label="Loading...">
        <img width={300} src={spinnerImg} alt="inline-spinner" />
        <span className="sr-only">Loading...</span>
      </div>
    );
  }

  return <SpinnerOverlayOnly />;
};

export const SpinnerOverlayOnly: React.FC = () => {
  const dvhSupported = CSS.supports("(height: 100dvh)");

  return (
    <div
      role="status"
      aria-live="assertive"
      aria-busy="true"
      aria-label="Loading..."
      style={{ backgroundColor: "rgba(0, 0, 0, 20%)" }}
      className={`flex justify-center items-center ${dvhSupported ? "h-[100dvh]" : "h-screen"}  w-screen fixed z-[1059] top-0 left-0 !m-0`}>
      <img src={spinnerImg} width={300} className="z-[1058]" alt="spinner" />
      <span className="sr-only">Loading content, please wait...</span>
    </div>
  );
};
